import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WorkspaceShell from "@/components/WorkspaceShell";
import { productByKey } from "@/lib/products";
import api from "@/lib/api";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { Building2, Loader2, Wand2, Download, Scan, Box } from "lucide-react";
import { ScanPanel, ViewerPanel } from "@/components/sahel/SahelPanels";

const PRODUCT = productByKey("sahel");

const TYPES = [
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "mosque", l: "Mosque" },
  { v: "school", l: "School" },
  { v: "warehouse", l: "Warehouse" },
];

const STARTERS = [
  "4-storey reinforced-concrete frame for a 200m² plot, Nigerian Building Code, family of 5",
  "Two-bedroom apartment in Dubai with majlis and prayer room, 95m²",
  "Single-storey clinic in Nairobi for outpatient + 4 consultation rooms",
  "Small mosque for 80 worshippers with separate women's section, 180m²",
];

export default function Sahel() {
  const [desc, setDesc] = useState("");
  const [type, setType] = useState("residential");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [bump, setBump] = useState(0);
  const [tab, setTab] = useState("generate");
  const [scannedDesign, setScannedDesign] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/sahel/designs").then((r) => setDesigns(r.data)).catch(() => {});
  }, []);

  const generate = async (text) => {
    const d = (text ?? desc).trim();
    if (!d || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/sahel/generate", { description: d, building_type: type });
      setResult(r.data);
      const refresh = await api.get("/sahel/designs");
      setDesigns(refresh.data);
      setBump((n) => n + 1);
      toast.success("Floor plan generated");
    } catch (e) {
      if (!handleQuotaError(e, nav)) {
        toast.error(e?.response?.data?.detail || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${result.floor_plan_b64}`;
    link.download = `sahel-floorplan.png`;
    link.click();
  };

  return (
    <WorkspaceShell product={PRODUCT} usageRefresh={bump}>
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {[["generate", Wand2, "Generate"], ["scan", Scan, "Scan to design"], ["viewer", Box, "2D / 3D viewer"]].map(([k, I, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sahel-tab-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-sm border text-sm font-medium transition-colors ${
              tab === k ? "border-foreground bg-card" : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"
            }`}
            style={tab === k ? { borderColor: PRODUCT.accent } : undefined}>
            <I className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "scan" && <ScanPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} onDesign={setScannedDesign} />}
      {tab === "viewer" && <ViewerPanel accent={PRODUCT.accent} design={scannedDesign} />}

      {tab === "generate" && (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-sm border border-border/60 bg-card p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Building description</div>
            <textarea
              data-testid="sahel-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={5}
              placeholder="Describe the structure, rooms, code requirements…"
              className="w-full resize-none rounded-sm border border-border bg-background p-3 text-sm outline-none focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10"
            />
            <div className="mt-4">
              <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Type</span>
              <select
                data-testid="sahel-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-sm border border-border bg-background text-sm outline-none"
              >
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <button
              data-testid="sahel-generate"
              onClick={() => generate()}
              disabled={loading || !desc.trim()}
              className="mt-5 inline-flex w-full h-11 items-center justify-center gap-2 rounded-sm text-white text-sm font-medium disabled:opacity-50"
              style={{ background: PRODUCT.gradient }}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              {loading ? "Drafting plan…" : "Generate floor plan"}
            </button>
          </div>

          <div className="rounded-sm border border-border/60 bg-card p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Try a brief</div>
            <div className="space-y-1.5">
              {STARTERS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`sahel-starter-${i}`}
                  onClick={() => setDesc(s)}
                  className="block w-full text-start text-xs rounded-sm bg-secondary/60 hover:bg-secondary px-3 py-2 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-8">
          <div className="rounded-sm border border-border/60 bg-card overflow-hidden">
            <div className="aspect-video bg-secondary/50 flex items-center justify-center relative">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-card/70 backdrop-blur-sm z-10">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" /> Drafting blueprint…
                  </div>
                </div>
              )}
              {result ? (
                <img
                  src={`data:image/png;base64,${result.floor_plan_b64}`}
                  alt="Floor plan"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center p-8">
                  <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <div className="font-display text-lg">Floor plan viewport</div>
                  <p className="text-sm text-muted-foreground mt-1">Describe a structure on the left to generate.</p>
                </div>
              )}
            </div>
            {result && (
              <div className="p-5 border-t border-border/60 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Specifications</div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{result.specs}</p>
                </div>
                <div className="flex md:flex-col gap-2 md:items-end">
                  <button
                    data-testid="sahel-download"
                    onClick={download}
                    className="inline-flex h-10 items-center gap-2 rounded-sm bg-foreground px-4 text-sm text-background"
                  >
                    <Download className="h-4 w-4" /> Download PNG
                  </button>
                  <span className="text-[11px] text-muted-foreground italic">DWG export coming soon</span>
                </div>
              </div>
            )}
          </div>

          {designs.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Recent designs</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {designs.map((d) => (
                  <button
                    key={d.id}
                    data-testid={`sahel-recent-${d.id}`}
                    onClick={() => setResult({ floor_plan_b64: d.image_b64, specs: d.specs })}
                    className="text-start rounded-sm border border-border/60 overflow-hidden bg-card hover:border-foreground/30 transition-colors"
                  >
                    <div className="aspect-video bg-secondary/40">
                      <img src={`data:image/png;base64,${d.image_b64}`} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5">
                      <div className="text-xs font-medium capitalize">{d.building_type}</div>
                      <div className="text-[11px] text-muted-foreground line-clamp-1">{d.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
      )}
    </WorkspaceShell>
  );
}
